import { Head, Link, useForm } from "@inertiajs/react";
import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  Boxes,
  CalendarDays,
  LockKeyhole,
  MapPin,
  Milestone,
  UserRound,
} from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { FormField } from "@/components/FormField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

type Member = { id: number; email: string };
type Task = {
  id: number;
  cost_code: string;
  name: string;
  planned_quantity: string;
  unit: string;
  budget_revenue: string;
  budget_cost?: string;
  progress_percent: string;
  starts_on: string;
  ends_on: string;
  assigned_to_id: number | null;
};
type Stage = {
  id: number;
  name: string;
  status: string;
  starts_on: string;
  ends_on: string;
  tasks: Task[];
};
type Project = {
  id: number;
  reference: string;
  name: string;
  status: string;
  starts_on: string;
  ends_on: string;
  budget_revenue: string;
  budget_cost?: string;
  margin_amount?: string;
  margin_percent?: string;
  progress_percent: string;
  manager_id: number | null;
  client: { name: string };
  enquiry: { reference: string; title: string };
  quotation: { id: number; reference: string; revision_number: number };
  stages: Stage[];
  milestones: {
    id: number;
    name: string;
    due_on: string;
    status: string;
    project_stage_id: number | null;
  }[];
};

export default function ProjectShow({
  project,
  can_manage,
  can_view_margin,
  members,
}: {
  project: Project;
  can_manage: boolean;
  can_view_margin: boolean;
  members: Member[];
}) {
  const projectForm = useForm({
    name: project.name,
    status: project.status,
    starts_on: project.starts_on,
    ends_on: project.ends_on,
    manager_id: project.manager_id?.toString() || "",
  });
  const finalStage = project.stages[project.stages.length - 1];
  const milestoneForm = useForm<{
    name: string;
    due_on: string;
    project_stage_id: string;
  }>({
    name: "",
    due_on: project.ends_on,
    project_stage_id: finalStage?.id.toString() || "",
  });
  const canComplete =
    Number(project.progress_percent) === 100 &&
    project.milestones.every((milestone) => milestone.status === "complete");
  return (
    <>
      <Head title={project.reference}>
        <meta
          name="description"
          content={`Project plan for ${project.name}.`}
        />
        <meta property="og:title" content={project.reference} />
        <meta
          property="og:description"
          content={`Project plan for ${project.name}.`}
        />
      </Head>
      <AppShell>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link href="/projects" className="inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Projects
          </Link>
          <div className="flex flex-wrap gap-4">
            <Link
              href={`/projects/${project.id}/progress-control`}
              className="inline-flex items-center gap-1"
            >
              <BarChart3 className="h-4 w-4" /> Progress control
            </Link>
            <Link
              href={`/projects/${project.id}/work-packages`}
              className="inline-flex items-center gap-1"
            >
              <Boxes className="h-4 w-4" /> Work packages
            </Link>
            <Link
              href={`/projects/${project.id}/setup`}
              className="inline-flex items-center gap-1"
            >
              <MapPin className="h-4 w-4" /> Locations & parties
            </Link>
            <Link
              href="/work-catalogue"
              className="inline-flex items-center gap-1"
            >
              <BookOpen className="h-4 w-4" /> Work catalogue
            </Link>
          </div>
        </div>
        <div className="mt-5 flex flex-col gap-4 border-b border-hairline pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-accent-display">
              {project.reference} · {project.status}
            </p>
            <h1 className="mt-2">{project.name}</h1>
            <p className="mt-2 text-sm text-ink-muted">
              {project.client.name} ·{" "}
              <Link href={`/estimating/quotations/${project.quotation.id}`}>
                {project.quotation.reference} Rev{" "}
                {project.quotation.revision_number}
              </Link>
            </p>
          </div>
          <div className="min-w-48">
            <p className="text-xs text-ink-muted">Plan progress foundation</p>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-hairline">
              <div
                className="h-full bg-accent"
                style={{ width: `${project.progress_percent}%` }}
              />
            </div>
            <p className="mt-1 text-right text-xs text-ink-muted">
              {Number(project.progress_percent).toFixed(1)}%
            </p>
          </div>
        </div>
        <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric
            label="Approved revenue"
            value={`AED ${Number(project.budget_revenue).toLocaleString()}`}
          />
          {can_view_margin ? (
            <>
              <Metric
                label="Approved cost"
                value={`AED ${Number(project.budget_cost).toLocaleString()}`}
                sensitive
              />
              <Metric
                label="Budget margin"
                value={`AED ${Number(project.margin_amount).toLocaleString()}`}
                sensitive
              />
              <Metric
                label="Margin rate"
                value={`${Number(project.margin_percent).toFixed(1)}%`}
                sensitive
              />
            </>
          ) : (
            <div className="rounded-md border border-hairline bg-surface p-4 sm:col-span-1 lg:col-span-3">
              <p className="flex items-center gap-2 font-medium text-ink-display">
                <LockKeyhole className="h-4 w-4 text-amber-700" /> Cost and
                margin restricted
              </p>
              <p className="mt-2 text-xs text-ink-muted">
                Approved scope and dates remain visible; internal budget cost is
                omitted by the server.
              </p>
            </div>
          )}
        </div>
        {can_manage && (
          <form
            className="mt-7 grid gap-4 rounded-md border border-hairline bg-surface p-5 sm:grid-cols-2 lg:grid-cols-5 lg:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              projectForm.patch(`/projects/${project.id}`);
            }}
          >
            <FormField label="Project name" error={projectForm.errors.name}>
              <Input
                className="mt-1"
                value={projectForm.data.name}
                onChange={(event) =>
                  projectForm.setData("name", event.target.value)
                }
              />
            </FormField>
            <FormField label="Manager" error={projectForm.errors.manager_id}>
              <Select
                className="mt-1"
                value={projectForm.data.manager_id}
                onChange={(event) =>
                  projectForm.setData("manager_id", event.target.value)
                }
              >
                <option value="">Unassigned</option>
                {members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.email}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Start" error={projectForm.errors.starts_on}>
              <Input
                className="mt-1"
                type="date"
                value={projectForm.data.starts_on}
                onChange={(event) =>
                  projectForm.setData("starts_on", event.target.value)
                }
              />
            </FormField>
            <FormField
              label="Finish"
              error={
                projectForm.errors.ends_on ||
                Object.values(projectForm.errors)[0]
              }
            >
              <Input
                className="mt-1"
                type="date"
                value={projectForm.data.ends_on}
                onChange={(event) =>
                  projectForm.setData("ends_on", event.target.value)
                }
              />
            </FormField>
            <div>
              <Select
                aria-label="Project status"
                value={projectForm.data.status}
                onChange={(event) =>
                  projectForm.setData("status", event.target.value)
                }
              >
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="on_hold">On hold</option>
                <option value="completed" disabled={!canComplete}>
                  Completed
                </option>
              </Select>
              <Button
                className="mt-2 w-full"
                type="submit"
                disabled={projectForm.processing}
              >
                Save plan
              </Button>
            </div>
          </form>
        )}
        <section className="mt-9">
          <h2>Stages & tasks</h2>
          <div className="mt-4 space-y-6">
            {project.stages.map((stage) => (
              <StageBlock
                key={stage.id}
                stage={stage}
                members={members}
                canManage={can_manage}
                canViewMargin={can_view_margin}
              />
            ))}
          </div>
        </section>
        <ProjectTimeline project={project} />
        <section className="mt-9 border-t border-hairline pt-7">
          <h2 className="flex items-center gap-2">
            <Milestone className="h-5 w-5" /> Milestones
          </h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {project.milestones.map((milestone) => (
              <MilestoneRow
                key={milestone.id}
                milestone={milestone}
                stages={project.stages}
                canManage={can_manage}
              />
            ))}
          </div>
          {can_manage && (
            <form
              className="mt-4 grid max-w-3xl gap-3 sm:grid-cols-[1fr_180px_1fr_auto] sm:items-end"
              onSubmit={(event) => {
                event.preventDefault();
                milestoneForm.post(`/projects/${project.id}/milestones`, {
                  onSuccess: () => milestoneForm.reset("name"),
                });
              }}
            >
              <FormField label="Milestone" error={milestoneForm.errors.name}>
                <Input
                  className="mt-1"
                  value={milestoneForm.data.name}
                  onChange={(event) =>
                    milestoneForm.setData("name", event.target.value)
                  }
                />
              </FormField>
              <FormField label="Due" error={milestoneForm.errors.due_on}>
                <Input
                  className="mt-1"
                  type="date"
                  value={milestoneForm.data.due_on}
                  onChange={(event) =>
                    milestoneForm.setData("due_on", event.target.value)
                  }
                />
              </FormField>
              <FormField
                label="Stage"
                error={milestoneForm.errors.project_stage_id}
              >
                <Select
                  className="mt-1"
                  value={milestoneForm.data.project_stage_id}
                  onChange={(event) =>
                    milestoneForm.setData(
                      "project_stage_id",
                      event.target.value,
                    )
                  }
                >
                  {project.stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
                </Select>
              </FormField>
              <Button
                type="submit"
                disabled={!milestoneForm.data.name || milestoneForm.processing}
              >
                Add
              </Button>
            </form>
          )}
        </section>
      </AppShell>
    </>
  );
}

function Metric({
  label,
  value,
  sensitive = false,
}: {
  label: string;
  value: string;
  sensitive?: boolean;
}) {
  return (
    <div className="rounded-md border border-hairline bg-surface p-4">
      <p className="flex items-center gap-1 text-xs text-ink-muted">
        {sensitive && <LockKeyhole className="h-3.5 w-3.5" />}
        {label}
      </p>
      <p className="mt-2 text-xl font-semibold text-ink-display">{value}</p>
    </div>
  );
}

function StageBlock({
  stage,
  members,
  canManage,
  canViewMargin,
}: {
  stage: Stage;
  members: Member[];
  canManage: boolean;
  canViewMargin: boolean;
}) {
  const form = useForm({
    name: stage.name,
    starts_on: stage.starts_on,
    ends_on: stage.ends_on,
    status: stage.status,
  });
  return (
    <article className="overflow-hidden rounded-md border border-hairline">
      {canManage ? (
        <form
          className="grid gap-3 bg-surface px-4 py-3 sm:grid-cols-2 lg:grid-cols-[1fr_160px_160px_150px_auto] lg:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            form.patch(`/project_stages/${stage.id}`);
          }}
        >
          <FormField label="Stage" error={form.errors.name}>
            <Input
              className="mt-1"
              value={form.data.name}
              onChange={(event) => form.setData("name", event.target.value)}
            />
          </FormField>
          <FormField label="Start" error={form.errors.starts_on}>
            <Input
              className="mt-1"
              type="date"
              value={form.data.starts_on}
              onChange={(event) =>
                form.setData("starts_on", event.target.value)
              }
            />
          </FormField>
          <FormField
            label="Finish"
            error={form.errors.ends_on || Object.values(form.errors)[0]}
          >
            <Input
              className="mt-1"
              type="date"
              value={form.data.ends_on}
              onChange={(event) => form.setData("ends_on", event.target.value)}
            />
          </FormField>
          <FormField label="Status" error={form.errors.status}>
            <Select
              className="mt-1"
              value={form.data.status}
              onChange={(event) => form.setData("status", event.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option
                value="complete"
                disabled={
                  !stage.tasks.every(
                    (task) => Number(task.progress_percent) === 100,
                  )
                }
              >
                Complete
              </option>
            </Select>
          </FormField>
          <Button type="submit" size="sm" disabled={form.processing}>
            Save stage
          </Button>
        </form>
      ) : (
        <header className="flex flex-col gap-2 bg-surface px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3>{stage.name}</h3>
            <p className="mt-1 flex items-center gap-1 text-xs text-ink-muted">
              <CalendarDays className="h-3.5 w-3.5" /> {stage.starts_on} →{" "}
              {stage.ends_on}
            </p>
          </div>
          <span className="text-xs capitalize text-ink-muted">
            {stage.status}
          </span>
        </header>
      )}
      <div className="divide-y divide-hairline">
        {stage.tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            members={members}
            canManage={canManage}
            canViewMargin={canViewMargin}
          />
        ))}
      </div>
    </article>
  );
}

function TaskRow({
  task,
  members,
  canManage,
  canViewMargin,
}: {
  task: Task;
  members: Member[];
  canManage: boolean;
  canViewMargin: boolean;
}) {
  const form = useForm({
    name: task.name,
    assigned_to_id: task.assigned_to_id?.toString() || "",
    starts_on: task.starts_on,
    ends_on: task.ends_on,
  });
  if (!canManage)
    return (
      <div className="grid gap-2 p-4 sm:grid-cols-[110px_1fr_auto]">
        <p className="text-xs font-semibold text-accent-display">{task.cost_code}</p>
        <div>
          <p className="font-medium text-ink-display">{task.name}</p>
          <p className="mt-1 text-xs text-ink-muted">
            {task.planned_quantity} {task.unit} · {task.starts_on} →{" "}
            {task.ends_on}
          </p>
        </div>
        <p className="text-right text-sm text-ink-muted">
          {task.progress_percent}%
        </p>
      </div>
    );
  return (
    <form
      className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-[110px_minmax(220px,1fr)_180px_145px_145px_auto] lg:items-end"
      onSubmit={(event) => {
        event.preventDefault();
        form.patch(`/project_tasks/${task.id}`);
      }}
    >
      <div>
        <p className="text-xs font-semibold text-accent-display">{task.cost_code}</p>
        <p className="mt-2 text-xs text-ink-muted">
          Revenue AED {Number(task.budget_revenue).toLocaleString()}
        </p>
        {canViewMargin && (
          <p className="text-xs text-ink-muted">
            Cost AED {Number(task.budget_cost).toLocaleString()}
          </p>
        )}
      </div>
      <FormField label="Task" error={form.errors.name}>
        <Input
          className="mt-1"
          value={form.data.name}
          onChange={(event) => form.setData("name", event.target.value)}
        />
      </FormField>
      <FormField label="Assigned to" error={form.errors.assigned_to_id}>
        <Select
          className="mt-1"
          value={form.data.assigned_to_id}
          onChange={(event) =>
            form.setData("assigned_to_id", event.target.value)
          }
        >
          <option value="">Unassigned</option>
          {members.map((member) => (
            <option key={member.id} value={member.id}>
              {member.email}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Start" error={form.errors.starts_on}>
        <Input
          className="mt-1"
          type="date"
          value={form.data.starts_on}
          onChange={(event) => form.setData("starts_on", event.target.value)}
        />
      </FormField>
      <FormField label="Finish" error={form.errors.ends_on}>
        <Input
          className="mt-1"
          type="date"
          value={form.data.ends_on}
          onChange={(event) => form.setData("ends_on", event.target.value)}
        />
      </FormField>
      <Button type="submit" size="sm" disabled={form.processing}>
        <UserRound className="h-4 w-4" /> Assign
      </Button>
    </form>
  );
}

function MilestoneRow({
  milestone,
  stages,
  canManage,
}: {
  milestone: Project["milestones"][number];
  stages: Stage[];
  canManage: boolean;
}) {
  const form = useForm({
    name: milestone.name,
    due_on: milestone.due_on,
    status: milestone.status,
    project_stage_id: milestone.project_stage_id?.toString() || "",
  });
  if (!canManage)
    return (
      <div className="rounded-md border border-hairline p-4">
        <p className="font-medium text-ink-display">{milestone.name}</p>
        <p className="mt-1 text-xs text-ink-muted">
          Due {milestone.due_on} · {milestone.status}
        </p>
      </div>
    );
  return (
    <form
      className="grid gap-3 rounded-md border border-hairline p-4 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        form.patch(`/project_milestones/${milestone.id}`);
      }}
    >
      <FormField label="Milestone" error={form.errors.name}>
        <Input
          className="mt-1"
          value={form.data.name}
          onChange={(event) => form.setData("name", event.target.value)}
        />
      </FormField>
      <FormField label="Due" error={form.errors.due_on}>
        <Input
          className="mt-1"
          type="date"
          value={form.data.due_on}
          onChange={(event) => form.setData("due_on", event.target.value)}
        />
      </FormField>
      <FormField label="Stage" error={form.errors.project_stage_id}>
        <Select
          className="mt-1"
          value={form.data.project_stage_id}
          onChange={(event) =>
            form.setData("project_stage_id", event.target.value)
          }
        >
          {stages.map((stage) => (
            <option key={stage.id} value={stage.id}>
              {stage.name}
            </option>
          ))}
        </Select>
      </FormField>
      <div>
        <Select
          aria-label="Milestone status"
          value={form.data.status}
          onChange={(event) => form.setData("status", event.target.value)}
        >
          <option value="pending">Pending</option>
          <option value="complete">Complete</option>
        </Select>
        <Button
          className="mt-2 w-full"
          type="submit"
          size="sm"
          disabled={form.processing}
        >
          Save milestone
        </Button>
      </div>
    </form>
  );
}

function ProjectTimeline({ project }: { project: Project }) {
  const start = dateNumber(project.starts_on);
  const finish = dateNumber(project.ends_on);
  const duration = Math.max(finish - start + 86_400_000, 86_400_000);
  const rows = project.stages.flatMap((stage) =>
    stage.tasks.map((task) => ({ ...task, stage: stage.name })),
  );
  return (
    <section className="mt-9">
      <div>
        <h2>Timeline / Gantt</h2>
        <p className="mt-1 text-sm text-ink-muted">
          The same approved stages and tasks, shown across the project calendar.
        </p>
      </div>
      <div className="mt-4 overflow-x-auto rounded-md border border-hairline">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-[220px_1fr] border-b border-hairline bg-surface text-xs text-ink-muted">
            <div className="p-3">Task</div>
            <div className="flex items-center justify-between border-l border-hairline px-3">
              <span>{project.starts_on}</span>
              <span>{project.ends_on}</span>
            </div>
          </div>
          {rows.map((task) => {
            const left = Math.max(
              0,
              ((dateNumber(task.starts_on) - start) / duration) * 100,
            );
            const right = Math.min(
              100,
              ((dateNumber(task.ends_on) - start + 86_400_000) / duration) *
                100,
            );
            const width = Math.max(right - left, 1);
            return (
              <div
                key={task.id}
                className="grid grid-cols-[220px_1fr] border-b border-hairline last:border-0"
              >
                <div className="p-3">
                  <p className="truncate text-xs font-semibold text-accent-display">
                    {task.cost_code} · {task.stage}
                  </p>
                  <p className="mt-1 truncate text-sm text-ink-display">
                    {task.name}
                  </p>
                </div>
                <div className="relative border-l border-hairline bg-[linear-gradient(to_right,var(--color-hairline)_1px,transparent_1px)] bg-[size:25%_100%] px-3 py-4">
                  <div
                    className="absolute top-1/2 h-6 -translate-y-1/2 rounded-sm bg-accent-faded ring-1 ring-inset ring-accent/30"
                    style={{
                      left: `calc(${left}% + 4px)`,
                      width: `calc(${width}% - 8px)`,
                    }}
                    aria-label={`${task.name}: ${task.starts_on} to ${task.ends_on}`}
                  >
                    <div
                      className="h-full rounded-sm bg-accent"
                      style={{ width: `${task.progress_percent}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          {rows.length === 0 && (
            <p className="p-5 text-sm text-ink-muted">No planned tasks yet.</p>
          )}
        </div>
      </div>
    </section>
  );
}

function dateNumber(value: string) {
  return new Date(`${value}T00:00:00Z`).getTime();
}
